import React, { useEffect, useState, useCallback } from "react";
import { Inbox, Trash2, CheckCircle2, ShoppingCart, Loader2, Package, SprayCan, Clock, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_CLS = { new: "bg-amber-100 text-amber-700", ordered: "bg-blue-100 text-blue-700", done: "bg-green-100 text-green-700" };
const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");

export default function ReorderInbox() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = useCallback(() => api.get("/admin/housekeeping/reorders").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/housekeeping/reorders/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this reorder request?")) return; try { await api.delete(`/admin/housekeeping/reorders/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const exportPdf = async () => {
    try { const r = await api.get(`/admin/housekeeping/reorders.pdf?status=${filter}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "rose-air-reorder-history.pdf"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch { toast.error("PDF unavailable"); }
  };
  const whatsapp = (r) => {
    const label = r.kind === "cleaning" ? "Cleaning products" : "Property inventory";
    const lines = (r.lines || []).map((l) => `- ${l.quantity} ${l.unit || ""} ${l.name}`.replace(/\s+/g, " ").trim()).join("\n");
    let text = `The Rose Airbnb — Restock request (${label})\n\n${lines}`;
    if (r.note) text += `\n\nNote: ${r.note}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (rows === null) return <Loader2 className="animate-spin text-rose mx-auto my-12" />;
  const list = rows.filter((r) => (filter === "open" ? (r.status || "new") !== "done" : filter === "all" ? true : (r.status || "new") === filter));

  return (
    <div data-testid="reorder-inbox">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><Inbox size={22} className="text-rose" /> Reorder inbox</h2>
        <div className="flex items-center gap-2">
          <button onClick={exportPdf} data-testid="reorder-export-pdf" className="text-sm px-3 py-2 rounded-lg bg-forest text-cream font-semibold inline-flex items-center gap-1.5"><FileDown size={14} /> Export PDF</button>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reorder-filter" className="text-sm rounded-lg border border-stone-200 px-3 py-2">
            <option value="open">Open</option><option value="new">New</option><option value="ordered">Ordered</option><option value="done">Done</option><option value="all">All</option>
          </select>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-16 text-stone-400"><Inbox size={30} className="mx-auto mb-3 opacity-50" />No reorder requests here.</div>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="card-lux p-4" data-testid={`reorder-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  {r.kind === "cleaning" ? <SprayCan size={16} className="text-teal-600" /> : <Package size={16} className="text-forest" />}
                  <div>
                    <div className="font-semibold text-forest">{r.kind === "cleaning" ? "Cleaning products" : "Property inventory"}</div>
                    <div className="text-xs text-stone-400">From {r.name} · {(r.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[r.status || "new"]}`}>{r.status || "new"}</span>
              </div>
              <div className="bg-sand/40 rounded-lg px-3 py-2 mb-3">
                {(r.lines || []).map((l, i) => (
                  <div key={i} className="text-sm text-forest flex justify-between"><span>{l.name}</span><span className="text-stone-500">{l.quantity} {l.unit}</span></div>
                ))}
                {r.note && <div className="text-xs text-stone-500 mt-2 pt-2 border-t border-stone-200">Note: {r.note}</div>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mb-3" data-testid={`reorder-timeline-${r.id}`}>
                <span className="text-stone-500 inline-flex items-center gap-1"><Clock size={11} /> Requested {fmt(r.created_at)}</span>
                {r.ordered_at && <span className="text-blue-600 inline-flex items-center gap-1"><ShoppingCart size={11} /> Ordered {fmt(r.ordered_at)}{r.ordered_by ? ` · ${r.ordered_by}` : ""}</span>}
                {r.done_at && <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Done {fmt(r.done_at)}{r.done_by ? ` · ${r.done_by}` : ""}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => whatsapp(r)} data-testid={`reorder-whatsapp-${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white font-semibold inline-flex items-center gap-1.5"><Send size={13} /> WhatsApp list</button>
                {(r.status || "new") !== "ordered" && <button onClick={() => setStatus(r.id, "ordered")} data-testid={`reorder-ordered-${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 font-semibold inline-flex items-center gap-1.5"><ShoppingCart size={13} /> Mark ordered</button>}
                {(r.status || "new") !== "done" && <button onClick={() => setStatus(r.id, "done")} data-testid={`reorder-done-${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-700 font-semibold inline-flex items-center gap-1.5"><CheckCircle2 size={13} /> Mark done</button>}
                <button onClick={() => del(r.id)} data-testid={`reorder-del-${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-600 font-semibold inline-flex items-center gap-1.5"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
